/* eslint-disable react/prop-types */
import { MapPinned, ArrowRight } from "lucide-react";

/**
 * "Request from the map" entry point on the document-services state picker.
 *
 * The state grid below assumes the visitor can name their state and then their
 * district / survey number. Plenty can't — they know the PLACE, not its record
 * identifiers. This is the way through for them.
 *
 * It opens the map in place (see MapRequestModal) rather than linking off to the
 * Maps page: the visitor came here to get a document, and sending them to a
 * different tool to do it loses most of them.
 *
 * Sits between the page's headline and a grid of photographic state cards, so it
 * is deliberately calm — a warm gold wash and one glowing accent tile rather
 * than another image — while still reading as the page's one alternative route.
 */
const MapRequestBanner = ({ onOpen }) => (
  <button
    type="button"
    onClick={onOpen}
    className="group relative mx-auto flex w-full max-w-2xl items-center gap-3.5 overflow-hidden rounded-2xl border border-[#1c4b93]/30 bg-gradient-to-r from-white via-white to-[#f2f6fb] px-4 py-3.5 text-left shadow-[0_1px_3px_rgba(154,107,0,0.10),0_12px_34px_-22px_rgba(21,53,104,0.36)] transition-all duration-300 hover:-translate-y-0.5 hover:border-gold/55 hover:shadow-[0_2px_6px_rgba(154,107,0,0.14),0_18px_44px_-20px_rgba(21,53,104,0.42)] sm:gap-4 sm:px-5 sm:py-4"
  >
    {/* Warm bloom that wakes up on hover — the page hero uses the same amber
        radial wash, so the banner reads as part of that surface. */}
    <span
      aria-hidden="true"
      className="pointer-events-none absolute -right-8 top-1/2 h-40 w-40 -translate-y-1/2 rounded-full opacity-60 transition-opacity duration-500 group-hover:opacity-100"
      style={{
        background:
          "radial-gradient(circle, rgba(28,75,147,0.16) 0%, transparent 70%)",
      }}
    />

    <span className="relative grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-gold to-orange text-white shadow-[0_6px_18px_-6px_rgba(255,169,0,0.9)] transition-transform duration-300 group-hover:scale-105 sm:h-12 sm:w-12">
      <MapPinned className="h-[19px] w-[19px] sm:h-[22px] sm:w-[22px]" />
    </span>

    <span className="relative min-w-0 flex-1">
      <span className="flex items-center gap-2">
        <span className="text-[13px] font-bold leading-snug text-primary sm:text-[15px]">
          Request document from map
        </span>
        <span className="hidden shrink-0 rounded-full bg-gold/15 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-[#9a6b00] sm:inline">
          Fastest
        </span>
      </span>
      <span className="mt-1 block text-[11px] font-medium leading-snug text-grey sm:text-[13px]">
        Point at the property and we&apos;ll take it from there
      </span>
    </span>

    <span className="relative grid h-8 w-8 shrink-0 place-items-center rounded-full border border-gold/40 bg-white/70 text-gold transition-all duration-300 group-hover:border-transparent group-hover:bg-gradient-to-br group-hover:from-gold group-hover:to-orange group-hover:text-white sm:h-9 sm:w-9">
      <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
    </span>
  </button>
);

export default MapRequestBanner;
